import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Campaign Service - Handles all operations related to campaigns
 */
const campaignService = {
  /**
   * Create a new campaign
   * @param {string} brandId - Brand user ID
   * @param {object} campaignData - Campaign data
   * @returns {Promise<object>} - Created campaign object with ID
   */
  createCampaign: async (brandId, campaignData) => {
    try {
      const campaignsRef = collection(db, 'campaigns');
      
      // Set default fields
      const defaultData = {
        brandId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: campaignData.status || 'draft',
        filledSpots: 0,
        analytics: {
          views: 0,
          applications: 0,
          approvedApplications: 0,
          submittedContent: 0,
          approvedContent: 0,
          totalReach: 0,
          totalEngagement: 0
        }
      };
      
      // Merge with provided data
      const dataToSave = { ...defaultData, ...campaignData };
      
      // Convert string dates to timestamps if provided as strings
      if (typeof dataToSave.deadline === 'string') {
        dataToSave.deadline = new Date(dataToSave.deadline);
      }
      
      if (typeof dataToSave.applicationDeadline === 'string') {
        dataToSave.applicationDeadline = new Date(dataToSave.applicationDeadline);
      }
      
      const docRef = await addDoc(campaignsRef, dataToSave);
      
      // Return the created campaign with its ID
      return { id: docRef.id, ...dataToSave };
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  },

  /**
   * Get campaign by ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object|null>} - Campaign data or null if not found
   */
  getCampaignById: async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignDoc = await getDoc(campaignRef);
      
      if (campaignDoc.exists()) {
        // Convert any Firestore timestamps to JS Date objects
        const data = campaignDoc.data();
        const campaign = { id: campaignDoc.id, ...data };
        
        // Format dates for easier handling
        if (campaign.createdAt && typeof campaign.createdAt.toDate === 'function') {
          campaign.createdAt = campaign.createdAt.toDate();
        }
        if (campaign.updatedAt && typeof campaign.updatedAt.toDate === 'function') {
          campaign.updatedAt = campaign.updatedAt.toDate();
        }
        if (campaign.startDate && typeof campaign.startDate.toDate === 'function') {
          campaign.startDate = campaign.startDate.toDate();
        }
        if (campaign.endDate && typeof campaign.endDate.toDate === 'function') {
          campaign.endDate = campaign.endDate.toDate();
        }
        if (campaign.applicationDeadline && typeof campaign.applicationDeadline.toDate === 'function') {
          campaign.applicationDeadline = campaign.applicationDeadline.toDate();
        }
        if (campaign.reviewDeadline && typeof campaign.reviewDeadline.toDate === 'function') {
          campaign.reviewDeadline = campaign.reviewDeadline.toDate();
        }
        

        return campaign;
      } else {

        return null;
      }
    } catch (error) {
      console.error('Error getting campaign:', error);
      throw error;
    }
  },

  /**
   * Update campaign
   * @param {string} campaignId - Campaign ID
   * @param {object} campaignData - Campaign data to update
   * @returns {Promise} - Promise that resolves when campaign is updated
   */
  updateCampaign: async (campaignId, campaignData) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      
      // Add updated timestamp
      campaignData.updatedAt = serverTimestamp();
      
      // Convert string dates to timestamps if provided as strings
      if (typeof campaignData.deadline === 'string') {
        campaignData.deadline = new Date(campaignData.deadline);
      }
      
      if (typeof campaignData.applicationDeadline === 'string') {
        campaignData.applicationDeadline = new Date(campaignData.applicationDeadline);
      }
      
      await updateDoc(campaignRef, campaignData);
      return { id: campaignId, ...campaignData };
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  },

  /**
   * Delete campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} - Promise that resolves when campaign is deleted
   */
  deleteCampaign: async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      await deleteDoc(campaignRef);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      throw error;
    }
  },

  /**
   * Get campaigns by brand ID
   * @param {string} brandId - Brand user ID
   * @param {object} options - Filter options
   * @returns {Promise<Array>} - Array of campaign objects
   */
  getCampaignsByBrandId: async (brandId, options = {}) => {
    try {
      const campaignsRef = collection(db, 'campaigns');
      let q = query(campaignsRef, where('brandId', '==', brandId));
      
      // Add status filter if provided
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }
      
      // Add sorting
      q = query(q, orderBy('createdAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const campaigns = [];
      querySnapshot.forEach((doc) => {
        campaigns.push({ id: doc.id, ...doc.data() });
      });
      
      return campaigns;
    } catch (error) {
      console.error('Error getting campaigns by brand ID:', error);
      throw error;
    }
  },

  /**
   * Get active campaigns for influencers
   * @param {object} filters - Filters (category, compensation type, etc.)
   * @param {object} options - Query options (limit, orderBy, etc.)
   * @returns {Promise<Array>} - Array of campaign objects
   */
  getActiveCampaigns: async (filters = {}, options = {}) => {
    try {
      const campaignsRef = collection(db, 'campaigns');
      
      // Start with base query for active campaigns
      let q = query(campaignsRef, where('status', '==', 'active'));
      
      // Add category filter if provided
      if (filters.category) {
        q = query(q, where('category', '==', filters.category));
      }
      
      // Add compensation type filter if provided
      if (filters.compensationType) {
        q = query(q, where('compensationType', '==', filters.compensationType));
      }
      
      // Add ordering
      const orderByField = options.orderBy || 'createdAt';
      const orderDirection = options.orderDirection || 'desc';
      q = query(q, orderBy(orderByField, orderDirection));
      
      // Add limit if provided
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const querySnapshot = await getDocs(q);
      
      const campaigns = [];
      querySnapshot.forEach((doc) => {
        campaigns.push({ id: doc.id, ...doc.data() });
      });
      
      // Filter out campaigns that have ended (past end date)
      const currentDate = new Date();
      let filteredCampaigns = campaigns.filter(campaign => {
        // Check if end date has passed
        let endDate;
        
        if (campaign.endDate instanceof Date) {
          endDate = campaign.endDate;
        } else if (campaign.endDate?.toDate) {
          endDate = campaign.endDate.toDate();
        } else if (typeof campaign.endDate === 'string') {
          endDate = new Date(campaign.endDate);
        } else {
          // If no valid end date, default to active
          return true;
        }
        
        return currentDate <= endDate;
      });
      
      // Apply additional client-side filtering if needed
      
      if (filters.minCompensation) {
        filteredCampaigns = filteredCampaigns.filter(campaign => 
          campaign.compensationValue >= filters.minCompensation
        );
      }
      
      if (filters.preferredPlatforms && filters.preferredPlatforms.length > 0) {
        filteredCampaigns = filteredCampaigns.filter(campaign => 
          campaign.preferredPlatforms.some(platform => 
            filters.preferredPlatforms.includes(platform)
          )
        );
      }
      
      return filteredCampaigns;
    } catch (error) {
      console.error('Error getting active campaigns:', error);
      throw error;
    }
  },

  /**
   * Update campaign analytics
   * @param {string} campaignId - Campaign ID
   * @param {object} analyticsData - Analytics data to update
   * @returns {Promise} - Promise that resolves when analytics are updated
   */
  updateCampaignAnalytics: async (campaignId, analyticsData) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignDoc = await getDoc(campaignRef);
      
      if (!campaignDoc.exists()) {
        throw new Error(`Campaign with ID ${campaignId} not found`);
      }
      
      const currentAnalytics = campaignDoc.data().analytics || {
        views: 0,
        applications: 0,
        approvedApplications: 0,
        submittedContent: 0,
        approvedContent: 0,
        totalReach: 0,
        totalEngagement: 0
      };
      
      // Merge with new analytics data
      const updatedAnalytics = { ...currentAnalytics, ...analyticsData };
      
      await updateDoc(campaignRef, {
        analytics: updatedAnalytics,
        updatedAt: serverTimestamp()
      });
      
      return updatedAnalytics;
    } catch (error) {
      console.error('Error updating campaign analytics:', error);
      throw error;
    }
  },

  /**
   * Increment campaign view count
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} - Promise that resolves when view count is incremented
   */
  incrementCampaignView: async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignDoc = await getDoc(campaignRef);
      
      if (!campaignDoc.exists()) {
        throw new Error(`Campaign with ID ${campaignId} not found`);
      }
      
      const currentViews = (campaignDoc.data().analytics?.views || 0) + 1;
      
      await updateDoc(campaignRef, {
        'analytics.views': currentViews,
        updatedAt: serverTimestamp()
      });
      
      return currentViews;
    } catch (error) {
      console.error('Error incrementing campaign view:', error);
      throw error;
    }
  }
};

/**
 * Utility function to check if a campaign is truly active
 * A campaign is considered active if:
 * 1. Its status is 'active' AND
 * 2. Its end date has not passed yet
 * 
 * @param {Object} campaign - The campaign object to check
 * @returns {boolean} - True if the campaign is active, false otherwise
 */
export const isCampaignActive = (campaign) => {
  if (!campaign) return false;
  
  // Check if status is active
  if (campaign.status !== 'active') return false;
  
  // Use current date as source of truth
  // This ensures we're using the correct date regardless of client/server time settings
  const currentDate = new Date();
  
  let endDate;
  
  try {
    if (campaign.endDate instanceof Date) {
      endDate = campaign.endDate;
    } else if (campaign.endDate?.toDate) {
      // Firebase Timestamp
      endDate = campaign.endDate.toDate();
    } else if (typeof campaign.endDate === 'string') {
      endDate = new Date(campaign.endDate);
    } else {
      // If no valid end date, default to inactive

      return false;
    }
    
    // Reset time to start of day for consistent comparison
    endDate.setHours(0, 0, 0, 0);
    
    // Compare dates correctly
    const isActive = currentDate <= endDate;

    return isActive;
  } catch (error) {
    console.error(`Error checking if campaign ${campaign.id || 'unknown'} is active:`, error);

    // Default to inactive if there's an error to avoid showing expired campaigns as active
    return false;
  }
};

export default campaignService;
